import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { AUDIT_PAGE_SIZE } from '@ready/shared';
import { useApi, type AuditEntry } from '@/lib/api';
import { formatDateTime } from '@/lib/dates';
import { useDebounced } from '@/lib/use-debounced';
import { Card, ErrorText } from '@/components/ui';
import { SearchBox } from '@/components/catalog';
import { Filter, Pager } from '@/components/people';

/**
 * Every recorded change to access, organizations and content, newest first:
 * search (action, who, organization, target), filter by area and recent
 * days, page through, and open an entry for its details.
 */
export function AdminAuditPage() {
  const api = useApi();
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const [area, setArea] = useState('');
  const [days, setDays] = useState('');
  const [page, setPage] = useState(0);
  const [areas, setAreas] = useState<string[]>([]);
  const query = {
    q: q || undefined,
    area: area || undefined,
    days: days ? Number(days) : undefined,
    offset: page * AUDIT_PAGE_SIZE,
    limit: AUDIT_PAGE_SIZE,
  };
  const logs = useQuery({
    queryKey: ['admin', 'audit', query],
    queryFn: () => api.admin.auditLogs(query),
    placeholderData: keepPreviousData,
  });
  // The area choices come with each first page; keep them while paging.
  useEffect(() => {
    if (logs.data?.areas) setAreas(logs.data.areas);
  }, [logs.data]);
  const refilter = (apply: () => void) => {
    apply();
    setPage(0);
  };
  const entries = logs.data?.logs ?? [];

  return (
    <Card>
      <div className="space-y-3">
        <SearchBox
          value={text}
          onChange={(v) => refilter(() => setText(v))}
          placeholder="Search actions, people, organizations or ids"
          label="Search the audit log"
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <Filter
            id="audit-area"
            label="Area"
            value={area}
            onChange={(v) => refilter(() => setArea(v))}
            options={[
              ['', 'Everything'],
              ...areas.map((a) => [a, a.replace(/_/g, ' ')] as [string, string]),
            ]}
          />
          <Filter
            id="audit-days"
            label="When"
            value={days}
            onChange={(v) => refilter(() => setDays(v))}
            options={[
              ['', 'Any time'],
              ['1', 'Last 24 hours'],
              ['7', 'Last 7 days'],
              ['30', 'Last 30 days'],
              ['90', 'Last 90 days'],
            ]}
          />
        </div>
      </div>
      <ErrorText error={logs.error} />
      <ul className="mt-4 divide-y divide-border">
        {logs.isPending && <li className="py-3 text-sm text-muted-foreground">Loading…</li>}
        {logs.isSuccess && entries.length === 0 && (
          <li className="py-3 text-sm text-muted-foreground">Nothing matches.</li>
        )}
        {entries.map((e) => (
          <Entry key={e.log.id} entry={e} />
        ))}
      </ul>
      <Pager
        page={page}
        pageSize={AUDIT_PAGE_SIZE}
        total={logs.data?.total ?? 0}
        busy={logs.isFetching}
        onPage={setPage}
      />
    </Card>
  );
}

/** One entry; open it for its target and details. */
function Entry({ entry: { log, actorEmail, actorName, orgName } }: { entry: AuditEntry }) {
  const [open, setOpen] = useState(false);
  const hasMore = Boolean(log.targetId || log.details);
  return (
    <li className="py-2.5 text-sm">
      <button
        type="button"
        disabled={!hasMore}
        aria-expanded={hasMore ? open : undefined}
        onClick={() => setOpen((o) => !o)}
        className="grid w-full gap-x-4 gap-y-0.5 text-left sm:grid-cols-[11rem_minmax(0,1fr)_minmax(0,1fr)_1.25rem]"
      >
        <span className="text-xs text-muted-foreground sm:text-sm">
          {formatDateTime(log.createdAt)}
        </span>
        <span className="font-mono text-xs sm:text-sm">{log.action}</span>
        <span className="min-w-0 break-all">
          {actorName ?? actorEmail ?? 'system'}
          {orgName && <span className="text-muted-foreground"> · {orgName}</span>}
        </span>
        {hasMore && (
          <ChevronDown
            className={`hidden size-4 text-muted-foreground transition-transform sm:block ${open ? 'rotate-180' : ''}`}
            aria-hidden
          />
        )}
      </button>
      {open && (
        <div className="mt-2 rounded-md bg-muted/50 p-3 text-xs">
          {log.targetType && (
            <p>
              <span className="text-muted-foreground">Target:</span> {log.targetType}{' '}
              <span className="font-mono">{log.targetId}</span>
            </p>
          )}
          {actorName && actorEmail && (
            <p>
              <span className="text-muted-foreground">By:</span> {actorEmail}
            </p>
          )}
          {log.details != null && (
            <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-all font-mono">
              {JSON.stringify(log.details, null, 2)}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}
